// i18n translations for Hiva AISTUDIO
// Supported languages: pt (Portuguese), en (English), fr (French)

export type Language = 'pt' | 'en' | 'fr';

export const translations = {
    // ============================================
    // COMMON / SHARED
    // ============================================
    common: {
        generate: { pt: 'Gerar', en: 'Generate', fr: 'Générer' },
        upload: { pt: 'Upload', en: 'Upload', fr: 'Télécharger' },
        loading: { pt: 'Carregando...', en: 'Loading...', fr: 'Chargement...' },
        credits: { pt: 'Créditos', en: 'Credits', fr: 'Crédits' },
        logout: { pt: 'Sair', en: 'Logout', fr: 'Déconnexion' },
        close: { pt: 'Fechar', en: 'Close', fr: 'Fermer' },
        settings: { pt: 'Configurações', en: 'Settings', fr: 'Paramètres' },
        buyCredits: { pt: 'Comprar Créditos', en: 'Buy Credits', fr: 'Acheter des crédits' },
    },

    // ============================================
    // MODES
    // ============================================
    modes: {
        commercial: { pt: 'Commercial', en: 'Commercial', fr: 'Commercial' },
        variations: { pt: 'Variações', en: 'Variations', fr: 'Variations' },
        stil: { pt: 'STIL', en: 'STIL', fr: 'STIL' },
        upscale: { pt: 'Upscale', en: 'Upscale', fr: 'Upscale' },
        video: { pt: 'Vídeo', en: 'Video', fr: 'Vidéo' },
    },

    // ============================================
    // GENERATOR VIEW
    // ============================================
    generator: {
        readyToCreate: { pt: 'Pronto para Criar', en: 'Ready to Create', fr: 'Prêt à Créer' },
        emptyStateText: {
            pt: 'Configure suas imagens de referência, ajuste o prompt e clique em Generate para gerar suas imagens.',
            en: 'Configure your reference images, adjust the prompt and click Generate to create your images.',
            fr: 'Configurez vos images de référence, ajustez le prompt et cliquez sur Générer pour créer vos images.',
        },
        describeVision: { pt: 'Descreva sua visão...', en: 'Describe your vision...', fr: 'Décrivez votre vision...' },
        creatingArtwork: { pt: 'Criando sua obra de arte...', en: 'Creating your artwork...', fr: 'Création de votre œuvre...' },
        insufficientCredits: {
            pt: 'Créditos insuficientes para gerar imagem.',
            en: 'Insufficient credits to generate image.',
            fr: 'Crédits insuffisants pour générer une image.'
        },
        model: { pt: 'Modelo', en: 'Model', fr: 'Modèle' },
        clothing: { pt: 'Roupa', en: 'Clothing', fr: 'Vêtement' },
        product: { pt: 'Produto', en: 'Product', fr: 'Produit' },
        sourceImage: { pt: 'Imagem Fonte', en: 'Source Image', fr: 'Image Source' },
        aspectRatio: { pt: 'Proporção', en: 'Aspect Ratio', fr: 'Format' },
        images: { pt: 'Imagens', en: 'Images', fr: 'Images' },
        variationDefault: { pt: 'Variação baseada na imagem anterior', en: 'Variation based on previous image', fr: 'Variation basée sur l\'image précédente' },
    },

    // ============================================
    // SETTINGS PANEL
    // ============================================
    settings: {
        mode: { pt: 'Modo', en: 'Mode', fr: 'Mode' },
        settings: { pt: 'Configurações', en: 'Settings', fr: 'Paramètres' },
        aspectRatio: { pt: 'Proporção', en: 'Aspect Ratio', fr: 'Format' },
        images: { pt: 'Imagens', en: 'Images', fr: 'Images' },
        model: { pt: 'Modelo', en: 'Model', fr: 'Modèle' },
        clothing: { pt: 'Roupa', en: 'Clothing', fr: 'Vêtement' },
        product: { pt: 'Produto', en: 'Product', fr: 'Produit' },
        sourceImage: { pt: 'Imagem Fonte', en: 'Source Image', fr: 'Image Source' },
        referenceImage: { pt: 'Imagem de Referência', en: 'Reference Image', fr: 'Image de référence' },
        general: { pt: 'Geral', en: 'General', fr: 'Général' },
        back: { pt: 'Costas', en: 'Back', fr: 'Dos' },
        change: { pt: 'Troca', en: 'Change', fr: 'Changer' },
        single: { pt: 'Único', en: 'Single', fr: 'Unique' },
        batch: { pt: 'Lote', en: 'Batch', fr: 'Lot' },
        upscaleHint: { pt: 'Envie uma imagem para aumentar a resolução.', en: 'Upload an image to upscale to higher resolution.', fr: 'Téléchargez une image pour augmenter la résolution.' },
        videoHint: { pt: 'Envie uma imagem para animar em vídeo.', en: 'Upload an image to animate into video.', fr: 'Téléchargez une image pour l\'animer en vidéo.' },
    },

    // ============================================
    // PROMPT BAR
    // ============================================
    promptBar: {
        generating: { pt: 'Gerando...', en: 'Generating...', fr: 'Génération...' },
    },

    // ============================================
    // PRICING MODAL
    // ============================================
    pricing: {
        choosePlan: { pt: 'Escolha seu Plano', en: 'Choose your Plan', fr: 'Choisissez votre forfait' },
        currentBalance: { pt: 'Saldo atual', en: 'Current balance', fr: 'Solde actuel' },
        mostPopular: { pt: 'MAIS POPULAR', en: 'MOST POPULAR', fr: 'LE PLUS POPULAIRE' },
        perMonth: { pt: '/mês', en: '/month', fr: '/mois' },
        custom: { pt: 'Sob Medida', en: 'Custom', fr: 'Sur Mesure' },
        selectPlan: { pt: 'Escolher Plano', en: 'Select Plan', fr: 'Choisir le forfait' },
        contactSpecialist: { pt: 'Falar com Especialista', en: 'Contact Specialist', fr: 'Contacter un spécialiste' },
        securePayment: {
            pt: 'Pagamento seguro via Kirvano. Créditos liberados instantaneamente.',
            en: 'Secure payment via Kirvano. Credits released instantly.',
            fr: 'Paiement sécurisé via Kirvano. Crédits libérés instantanément.'
        },
    },

    // ============================================
    // IMAGE GALLERY
    // ============================================
    gallery: {
        download: { pt: 'Baixar', en: 'Download', fr: 'Télécharger' },
        createVariation: { pt: 'Criar Variação', en: 'Create Variation', fr: 'Créer une variation' },
        close: { pt: 'Fechar', en: 'Close', fr: 'Fermer' },
        result: { pt: 'Resultado', en: 'Result', fr: 'Résultat' },
        enlargedView: { pt: 'Visualização ampliada', en: 'Enlarged view', fr: 'Vue agrandie' },
        errorTitle: { pt: 'Erro na Geração', en: 'Generation Error', fr: 'Erreur de génération' },
        waitMessage: { pt: 'Isso pode levar alguns segundos...', en: 'This may take a few seconds...', fr: 'Cela peut prendre quelques secondes...' },
    },

    // ============================================
    // LANDING PAGE
    // ============================================
    landing: {
        heroTitle: { pt: 'Crie. Gere. Domine.', en: 'Create. Generate. Dominate.', fr: 'Créez. Générez. Dominez.' },
        heroSubtitle: {
            pt: 'Transforme suas fotos de produto em campanhas de nível mundial. Faça o upload, descreva a cena e deixe nossa IA criar imagens de moda em segundos.',
            en: 'Transform your product photos into world-class campaigns. Upload, describe the scene, and let our AI create fashion images in seconds.',
            fr: 'Transformez vos photos de produits en campagnes de classe mondiale. Téléchargez, décrivez la scène et laissez notre IA créer des images de mode en quelques secondes.',
        },
        startCreating: { pt: 'Começar a Criar', en: 'Start Creating', fr: 'Commencer à créer' },
        viewPlans: { pt: 'Ver Planos', en: 'View Plans', fr: 'Voir les forfaits' },
        accessStudio: { pt: 'ACESSAR O STUDIO', en: 'ACCESS STUDIO', fr: 'ACCÉDER AU STUDIO' },
    },
};

// Helper function to get translation
// Helper function to get translation
export const t = (key: string, lang: Language): string => {
    const keys = key.split('.');

    // Safety check for depth to avoid runtime errors, though type system is hard here 
    // without deep recursion types.
    // For now, we keep it dynamic but avoid 'any' by using unknown and type narrowing if feasible,
    // or just suppress it with a cleaner comment if truly unavoidable, giving strictness options.

    // Better approach: recursive traversal with minimal type looseness
    let value: unknown = translations;

    for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
            value = (value as Record<string, unknown>)[k];
        } else {
            return key; // Return key if translation not found
        }
    }

    if (value && typeof value === 'object' && lang in value) {
        return (value as Record<string, string>)[lang] || key;
    }

    return key;
};

// Language labels for UI
export const languageLabels: Record<Language, string> = {
    pt: 'Português',
    en: 'English',
    fr: 'Français',
};
